/**
 * Phase 22 Plan 22-03 — TanStack Query v5 hooks for Client management.
 *
 * Mirrors useWorkOrderQueries.ts pattern.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  createClientRequest,
  deactivateClientRequest,
  getClientRequest,
  listClientsRequest,
  reactivateClientRequest,
  updateClientRequest,
} from '@/services/clients';
import type {
  ClientDto,
  ClientListItemDto,
  ClientListQuery,
  CreateClientBody,
  UpdateClientBody,
} from '@/types/client';
import type { PaginatedResult } from '@/types/roles';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** Paginated list of clients. */
export function useClientsQuery(query: ClientListQuery = {}) {
  return useQuery<PaginatedResult<ClientListItemDto>, ApiError>({
    queryKey: ['clients', 'list', query],
    queryFn: () => listClientsRequest(query),
    staleTime: 30_000,
  });
}

/** Full detail for a single client. */
export function useClientQuery(id: number) {
  return useQuery<ClientDto, ApiError>({
    queryKey: ['clients', 'detail', id],
    queryFn: () => getClientRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

/** Create a new client. */
export function useCreateClientMutation() {
  const qc = useQueryClient();
  return useMutation<ClientDto, ApiError, CreateClientBody>({
    mutationFn: (body) => createClientRequest(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

interface UpdateClientVars {
  readonly id: number;
  readonly body: UpdateClientBody;
}

/** Update an existing client. */
export function useUpdateClientMutation() {
  const qc = useQueryClient();
  return useMutation<ClientDto, ApiError, UpdateClientVars>({
    mutationFn: ({ id, body }) => updateClientRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

/** Deactivate a client (soft-delete). */
export function useDeactivateClientMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, number>({
    mutationFn: async (id) => { await deactivateClientRequest(id); return undefined; },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

/** Reactivate a previously deactivated client. */
export function useReactivateClientMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, number>({
    mutationFn: async (id) => { await reactivateClientRequest(id); return undefined; },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
